import { Box, FormControlLabel, InputAdornment, Radio, RadioGroup, TextField, Typography } from '@mui/material'
import { useState } from 'react'
import { toast } from 'react-toastify'
import { type TrackProfessionalDevelopmentResourceRequest } from '../../../../../../models/requestObjects/priorityOne/professionalDevelopmentResource/professionalDevelopmentResourceRequestObjects'
import { type ProfessionalDevelopmentResource } from '../../../../../../models/ProfessionalDevelopmentResource'
import { Modal } from '../../../../../customComponents/Modal'
import { type FieldError } from '../../../../../../hooks/use-fetch'
import { TrackProfessionalDevelopmentResource } from '../../../../../../services/ProfessionalDevelopmentResourceService'

interface Props {
  resource: ProfessionalDevelopmentResource
  onClose: () => void
  onSave: () => void
}

interface TrackProfessionalDevelopmentResourceForm {
  utilized: boolean | undefined
  actualAmount?: string
}

export function TrackProfessionalResourceDevelopmentResourceModal (props: Props): JSX.Element {
  const [fieldErrors, setFieldErrors] = useState<FieldError[]>([])
  const [formValues, setFormValues] = useState<TrackProfessionalDevelopmentResourceForm>({ utilized: props.resource.utilized, actualAmount: String(props.resource?.actualAmount ?? '') })

  const handleSave = async (): Promise<void> => {
    const completedForm = { ...formValues }
    if (!validateForm(completedForm)) {
      return
    }
    await trackProfessionalDevelopmentResource(completedForm)
    toast.success('Resource Tracked')
    props.onSave()
  }

  async function trackProfessionalDevelopmentResource (completedForm: TrackProfessionalDevelopmentResourceForm): Promise<void> {
    if (completedForm.utilized == null) {
      toast.error('Please indicate if the resource was utilized')
      return
    }

    const request: TrackProfessionalDevelopmentResourceRequest = {
      id: props.resource.id,
      utilized: completedForm.utilized,
      actualAmount: completedForm.actualAmount != null && completedForm.actualAmount !== '' ? Number(completedForm.actualAmount.toString().replace(',', '')) : undefined
    }

    const { success, errors } = await TrackProfessionalDevelopmentResource(request)
    if (!success) {
      errors.forEach((error) => {
        toast.error(error)
      })
      return
    }
    props.onClose()
  }

  function validateForm (completedForm: TrackProfessionalDevelopmentResourceForm): boolean {
    const notNumber: boolean = completedForm.actualAmount != null && isNaN(Number(completedForm.actualAmount.toString().replace(',', '')))
    if (notNumber) {
      toast.error('Budget must be a number greater than 0')
      return false
    }

    return true
  }

  const handleFieldUpdate = (e: React.ChangeEvent<HTMLInputElement>): void => {
    setFormValues({
      ...formValues,
      [e.target.name]: e.target.value
    })

    setFieldErrors(fieldErrors.filter(f => f.fieldName !== e.target.name))
  }

  const content = <Box sx={{ width: '350px' }}>
    <Typography variant='h5' sx={{ mb: '1em' }}>Resource</Typography>
    <Typography variant='body1' sx={{ my: '1em' }}>Was the resource utilized?</Typography>
    <RadioGroup
      row
      name="utilized"
      value={String(formValues.utilized)}
      onChange={handleFieldUpdate}
      aria-labelledby="demo-radio-buttons-group-label"
    >
      <FormControlLabel
        data-testid="utilized"
        value="true"
        control={<Radio />}
        label="Yes"
      />
      <FormControlLabel
        data-testid="notUtilized"
        value="false"
        control={<Radio />}
        label="No"
      />
    </RadioGroup>

    <Typography variant='h5' sx={{ mb: '1em' }}>Budget</Typography>
    <Typography variant='body1' sx={{ my: '1em' }}>How much funding was actually used? <Box component="span" sx={{ fontStyle: 'italic' }}>optional</Box></Typography>
    <TextField
      sx={{ width: '250px' }}
      variant='filled'
      onChange={handleFieldUpdate}
      label='Budget'
      data-testid='budgetUsed'
      InputProps={{ startAdornment: <InputAdornment position="start">$</InputAdornment> }}
      name={'actualAmount'}
      value={formValues.actualAmount}
    />

  </Box>

  return <Modal
    open={true}
    maxWidth='md'
    title='Additional Resource Tracking'
    confirmButtonText='Save'
    data-testid='trackResourceModal'
    bodyContent={content}
    onClose={props.onClose}
    onConfirm={handleSave}
  />
}
